import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { CustomButton } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { CustomButtonWithOutLoader } from "components/CustomComponents/CustomButton";
import { ApplyShiftPenaltyPolicy } from "./Deductions/ApplyShiftPenaltyPolicy";
import { ApplyBreakPenaltyPolicy } from "./Deductions/ApplyBreakPenaltyPolicy";
import { DeductSalaryForAbsence } from "./Deductions/DeductSalaryForAbsence";
import { LeaveToAbsentConversion } from "./Deductions/LeaveToAbsentConversion";

const normalizeDeductionPriorityRows = (rows, defaultPriorityRows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return defaultPriorityRows;
  }

  return rows
    .map((row) => ({
      category:
        row?.category ??
        row?.name ??
        row?.deduction_type ??
        row?.band_name ??
        row?.overtime_band?.band_name ??
        row?.overtime_band_name ??
        "",
    }))
    .filter((row) => row.category);
};

const getPaymentFactorBandName = (row) =>
  row?.band_name ??
  row?.overtime_band?.band_name ??
  row?.overtime_band_name ??
  row?.name ??
  "";

const buildDeductionPriorityRows = (earningData, defaultPriorityRows) => {
  const categoryRows = [
    {
      rows: earningData?.ot_payment_factor_configurations,
      suffix: "OT",
    },
    {
      rows: earningData?.weekoff_payment_factor_configurations,
      suffix: "WO",
    },
    {
      rows: earningData?.holiday_payment_factor_configurations,
      suffix: "HD",
    },
  ]
    .flatMap(({ rows, suffix }) =>
      Array.isArray(rows)
        ? rows
            .map((row) => getPaymentFactorBandName(row))
            .filter(Boolean)
            .map((bandName) => ({ category: `${bandName} ${suffix}` }))
        : [],
    )
    .filter(
      (row, index, rows) =>
        rows.findIndex((item) => item.category === row.category) === index,
    );

  const rowsWithAttendance = [...categoryRows, { category: "Attendance" }];

  return categoryRows.length > 0 ? rowsWithAttendance : defaultPriorityRows;
};

export const Deductions = ({
  handleBack,
  onUpdate,
  handleNext,
  data,
  earningData,
  complianceData,
  sftPenaltyPolicy,
  isAbsencePaid,
  setClearAbsenceValues,
}) => {
  const isBandAllowed = complianceData?.is_band_allowed === true;
  const defaultDeductionPriorityRows = useMemo(
    () => [{ category: "Attendance" }],
    [],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    resetField,
    formState: { errors },
  } = useForm({
    defaultValues: {
      is_apply_sft_penalty_policy: false,
      penalty_options: "hours",
      dedctions_options: "Attendance",
      attendance_first_mins: 0,
      attendance_late_exit: 0,
      attendance_initial_mins: 0,
      attendance_additional_mins: 0,
      fixed_amount_early_late: 0,
      fixed_amount_deduct_amount: 0,
      fixed_amount_extra_mins: 0,
      fixed_amount_early_late_deduct_amt: 0,
      sft_penalty_early_late_mins: 0,
      sft_penalty_deduct: 0,
      sft_penalty_additional_mins: 0,
      sft_penalty_sft_deduct: 0,
      sft_penalty_count_thershold: 0,
      sft_penalty_count_deduct: 0,
      sft_penalty_count_additional_mins: 0,
      sft_penalty_count_sft_deduct: 0,
      is_apply_break_penalty_policy: false,
      is_deduct_break_penalty_policy: "always",
      extra_break_minutes: 0,
      break_deduction: 0,
      additional_break_minutes: 0,
      additional_break_deduction: 0,
      deductions_attendance_deduct_type: "",
      deductions_fixed_amount_deduct_type: "",
      break_penalty_deduct_type: "",
      deduct_salary_for_absence: false,
      deduction: 0,
      additional_deduction: 0,
      extra_deduction: 0,
      extra_additional_deduction: 0,
      is_absence_penalty_band_hours_paid: false,
      is_deduction_priority_enabled: false,
      deduction_priority_list: defaultDeductionPriorityRows,
      trigger: true,
      consider_unpaid_leave_for_absent_penalty: false,
      is_leave_to_absent_convert: false,
      is_leave_to_absent_convert_count: 10,
      apply_penalty_count: 1,
    },
  });
  const [absencePaid, setAbsencePaid] = useState(isAbsencePaid ?? false);
  const [absenceDeductionPriorityRows, setAbsenceDeductionPriorityRows] =
    useState(defaultDeductionPriorityRows);
  const earningDeductionPriorityRows = useMemo(
    () => buildDeductionPriorityRows(earningData, defaultDeductionPriorityRows),
    [earningData, defaultDeductionPriorityRows],
  );

  const hanldePenaltyOptions = (event) => {
    const value = event.target.value;
    setValue("penalty_options", value);
    if (value === "hours") {
      resetSftPenalty();
      resetCountPenalty();
    } else if (value === "shift") {
      resetAttendanceSftPenalty();
      resetCountPenalty();
    } else {
      resetAttendanceSftPenalty();
      resetSftPenalty();
    }
  };

  const hanldeDeductionOptions = (event) => {
    setValue("dedctions_options", event.target.value);
    if (event.target.value === "Attendance") {
      resetFixedAmtSftPenalty();
    } else {
      resetAttendanceSftPenalty();
    }
  };

  const handleDeductBreakPenaltyPolicy = (event) => {
    setValue("is_deduct_break_penalty_policy", event.target.value);
  };

  const onSubmit = async (formValues) => {
    onUpdate(formValues);
    handleNext(3);
  };

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      reset({
        is_leave_to_absent_convert: false,
        is_leave_to_absent_convert_count: 10,
        apply_penalty_count: 1,
        ...data,
      });
      const absenceRows = normalizeDeductionPriorityRows(
        data?.absence_deduction_priority_list ?? data?.deduction_priority_list,
        defaultDeductionPriorityRows,
      );

      setAbsenceDeductionPriorityRows(absenceRows);
    }
  }, [data, reset, defaultDeductionPriorityRows]);

  useEffect(() => {
    setValue("deduction_priority_list", absenceDeductionPriorityRows, {
      shouldValidate: false,
    });
  }, [absenceDeductionPriorityRows, setValue]);

  const resetFixedAmtSftPenalty = () => {
    const fields = [
      "fixed_amount_early_late",
      "fixed_amount_deduct_amount",
      "fixed_amount_extra_mins",
      "fixed_amount_early_late_deduct_amt",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("deductions_fixed_amount_deduct_type", "");
  };

  const resetAttendanceSftPenalty = useCallback(() => {
    const fields = [
      "attendance_first_mins",
      "attendance_late_exit",
      "attendance_initial_mins",
      "attendance_additional_mins",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("deductions_attendance_deduct_type", "");
  }, [resetField, setValue]);

  const resetSftPenalty = useCallback(() => {
    const fields = [
      "sft_penalty_early_late_mins",
      "sft_penalty_deduct",
      "sft_penalty_additional_mins",
      "sft_penalty_sft_deduct",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
  }, [resetField, setValue]);

  const resetCountPenalty = useCallback(() => {
    const fields = [
      "sft_penalty_count_thershold",
      "sft_penalty_count_deduct",
      "sft_penalty_count_additional_mins",
      "sft_penalty_count_sft_deduct",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
  }, [resetField, setValue]);

  useEffect(() => {
    if (sftPenaltyPolicy) {
      setValue("is_apply_sft_penalty_policy", false);
      setValue("is_apply_break_penalty_policy", false);
      setValue("penalty_options", "hours");
      setValue("dedctions_options", "Attendance");
      resetAttendanceSftPenalty();
      resetSftPenalty();
      resetCountPenalty();
      setValue("is_deduct_break_penalty_policy", "always");
      setValue("break_penalty_deduct_type", "");
      [
        "extra_break_minutes",
        "break_deduction",
        "additional_break_minutes",
        "additional_break_deduction",
      ].forEach((field) => {
        resetField(field, { keepError: false });
        setValue(field, 0, { shouldValidate: false });
      });
      // setValue("trigger", false);
    }
  }, [
    absencePaid,
    isAbsencePaid,
    resetAttendanceSftPenalty,
    resetCountPenalty,
    resetField,
    resetSftPenalty,
    setClearAbsenceValues,
    setValue,
    sftPenaltyPolicy,
    watch,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <ApplyShiftPenaltyPolicy
            sftPenaltyPolicy={sftPenaltyPolicy}
            watch={watch}
            setValue={setValue}
            register={register}
            errors={errors}
            resetAttendanceSftPenalty={resetAttendanceSftPenalty}
            resetSftPenalty={resetSftPenalty}
            resetCountPenalty={resetCountPenalty}
            hanldePenaltyOptions={hanldePenaltyOptions}
            hanldeDeductionOptions={hanldeDeductionOptions}
          />
          <ApplyBreakPenaltyPolicy
            sftPenaltyPolicy={sftPenaltyPolicy}
            watch={watch}
            setValue={setValue}
            register={register}
            errors={errors}
            resetField={resetField}
            handleDeductBreakPenaltyPolicy={handleDeductBreakPenaltyPolicy}
          />
          <DeductSalaryForAbsence
            watch={watch}
            setValue={setValue}
            register={register}
            errors={errors}
            resetField={resetField}
            isBandAllowed={isBandAllowed}
            absencePaid={absencePaid}
            setAbsencePaid={setAbsencePaid}
            setClearAbsenceValues={setClearAbsenceValues}
            earningDeductionPriorityRows={earningDeductionPriorityRows}
            setAbsenceDeductionPriorityRows={setAbsenceDeductionPriorityRows}
          />
          <LeaveToAbsentConversion
            watch={watch}
            setValue={setValue}
            register={register}
            errors={errors}
          />
          <Box
            sx={{
              ml: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <CustomButton
                actionName="Prev"
                typeName="button"
                actionFuntion={() => {
                  handleBack(1);
                }}
              />
            </Box>
            <Box>
              {absencePaid && watch("deduct_salary_for_absence") ? (
                <CustomButtonWithOutLoader
                  actionName="Next"
                  disableAction={true}
                />
              ) : (
                <CustomButton actionName="Next" typeName="submit" />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
